import { useEffect, useRef, useState } from "react";
import { isFiatLeg, offeredTokens, tokenBySymbol } from "../lib/tokens";
import { TokenIcon } from "./TokenIcon";

interface TokenSelectProps {
  value: string;
  onChange: (symbol: string) => void;
  /** aria-label for the trigger button, e.g. "Sell token". */
  label: string;
  symbols?: readonly string[] | undefined;
}

const QUOTE_ONLY_TITLE =
  "Display-only reference leg — quotable, but no deployed token to settle against";

/** Marks a fiat reference leg: unmarked, EUR looks like EURC, and its order can never fill. */
function QuoteOnlyChip() {
  return (
    <span className="quote-only-chip" title={QUOTE_ONLY_TITLE}>
      quote only
    </span>
  );
}

/**
 * Dark token selector pill: icon, symbol + name, chevron. Opens a dark
 * dropdown panel; closes on outside click / Escape / selection.
 */
export function TokenSelect({ value, onChange, label, symbols }: TokenSelectProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selected = tokenBySymbol(value);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="token-select" ref={rootRef}>
      <button
        type="button"
        className="token-select-trigger"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <TokenIcon token={selected} size={28} />
        <span className="token-trigger-text">
          <span className="token-symbol">{selected.symbol}</span>
          <span className="token-name">{selected.name}</span>
        </span>
        {isFiatLeg(selected.symbol) && <QuoteOnlyChip />}
        <svg
          className={open ? "chevron chevron-open" : "chevron"}
          width="12"
          height="12"
          viewBox="0 0 12 12"
          aria-hidden="true"
        >
          <path
            d="M2 4l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.6"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <ul className="token-select-menu" role="listbox" aria-label={label}>
          {offeredTokens().filter((token) => symbols === undefined || symbols.includes(token.symbol)).map((token) => (
            <li key={token.symbol} role="none">
              <button
                type="button"
                role="option"
                aria-selected={token.symbol === value}
                className={
                  token.symbol === value
                    ? "token-option token-option-active"
                    : "token-option"
                }
                onClick={() => {
                  onChange(token.symbol);
                  setOpen(false);
                }}
              >
                <TokenIcon token={token} size={26} />
                <span className="token-option-text">
                  <span className="token-symbol">{token.symbol}</span>
                  <span className="token-name">{token.name}</span>
                </span>
                {isFiatLeg(token.symbol) && <QuoteOnlyChip />}
                {token.symbol === value && (
                  <svg
                    className="token-check"
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    aria-hidden="true"
                  >
                    <path
                      d="M2.5 7.5l3 3 6-6.5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      fill="none"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
